/**
 * Build Trial Balance Mapping / Amounts payloads from engagement ZIP JSON
 * (Juggernaut_Frontend.json and/or FinData_TrialBalance_Mapping.json),
 * matching the shape produced by scripts/process_trial_balance_mapping.py.
 */

export type TrialBalanceMappingRow = {
  bookName: string;
  ledgerId: number;
  ledgerName: string;
  noteLine: string;
  noteLineId: number;
  fsSubLine: string;
  fsSubLineId: number;
  fsLine: string;
  fsLineId: number;
  type: string;
  par: string;
  mapping: string;
};

export type TrialBalanceAmountsRow = {
  bookName: string;
  ledgerId: number;
  ledgerName: string;
  balances: Record<string, number>;
};

function parseDateKeyForSort(dateStr: string): number {
  try {
    const parts = String(dateStr).trim().split("/");
    if (parts.length < 3) return 0;
    const dd = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    let yy = parseInt(parts[2], 10);
    if (Number.isNaN(dd) || Number.isNaN(mm) || Number.isNaN(yy)) return 0;
    const year = yy < 100 ? 2000 + yy : yy;
    return year * 10000 + mm * 100 + dd;
  } catch {
    return 0;
  }
}

function looksLikeLedgerEntry(obj: unknown): boolean {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return false;
  const o = obj as Record<string, unknown>;
  return (
    "ledger_name" in o ||
    "ledgerName" in o ||
    "LedgerName" in o ||
    "ledger_id" in o ||
    "ledgerId" in o ||
    "LedgerId" in o
  );
}

/** Redux-persist and similar stores often JSON-stringify nested state. */
function maybeJsonParse(val: unknown): unknown {
  if (typeof val !== "string") return val;
  const t = val.trim();
  if ((t.startsWith("{") && t.endsWith("}")) || (t.startsWith("[") && t.endsWith("]"))) {
    try {
      return JSON.parse(t);
    } catch {
      return val;
    }
  }
  return val;
}

function tryAsBookLedgerMappingDict(obj: unknown): Record<string, any[]> | null {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const o = obj as Record<string, unknown>;
  const dict: Record<string, any[]> = {};
  for (const [k, v] of Object.entries(o)) {
    if (!Array.isArray(v)) continue;
    if (v.length === 0) {
      dict[k] = [];
      continue;
    }
    const hasLedgerRow = v.some((item) => looksLikeLedgerEntry(item));
    if (!hasLedgerRow) continue;
    dict[k] = v as any[];
  }
  return Object.keys(dict).length > 0 ? dict : null;
}

function deepFindMappingDict(node: unknown, depth: number, maxDepth: number): Record<string, any[]> | null {
  if (depth > maxDepth || node == null) return null;
  const parsed = typeof node === "string" ? maybeJsonParse(node) : node;
  if (parsed == null || typeof parsed !== "object") return null;
  if (Array.isArray(parsed)) {
    for (const item of parsed) {
      const found = deepFindMappingDict(item, depth + 1, maxDepth);
      if (found) return found;
    }
    return null;
  }
  const asDict = tryAsBookLedgerMappingDict(parsed);
  if (asDict) {
    const rowCount = Object.values(asDict).reduce((n, arr) => n + arr.length, 0);
    if (rowCount > 0) return asDict;
  }
  for (const v of Object.values(parsed as Record<string, unknown>)) {
    const found = deepFindMappingDict(v, depth + 1, maxDepth);
    if (found) return found;
  }
  return null;
}

/**
 * Resolve the `{ [bookName]: ledgerEntry[] }` object from archive JSON,
 * whether the file is the raw mapping or a wrapper (e.g. Juggernaut_Frontend.json).
 */
export function extractMappingDictFromArchiveJson(raw: unknown): Record<string, any[]> | null {
  if (raw == null) return null;
  const unwrapped = maybeJsonParse(raw);
  if (unwrapped == null || typeof unwrapped !== "object") return null;
  const candidates: unknown[] = [unwrapped];
  if (!Array.isArray(unwrapped)) {
    const r = unwrapped as Record<string, unknown>;
    for (const v of Object.values(r)) {
      const p = maybeJsonParse(v);
      if (p !== v && p != null && typeof p === "object") {
        candidates.push(p);
      }
    }
    const nestedKeys = [
      "FinData_TrialBalance_Mapping",
      "trialBalanceMapping",
      "TrialBalanceMapping",
      "mapping",
      "Juggernaut_Frontend",
      "juggernautFrontend",
      "juggernaut_frontend",
      "trial_balance_mapping",
      "finDataTrialBalanceMapping",
    ];
    for (const k of nestedKeys) {
      if (r[k] != null) candidates.push(maybeJsonParse(r[k]));
    }
    for (const wrap of [
      "state",
      "data",
      "financialData",
      "financial_data",
      "finData",
      "fin_data",
      "financials",
    ] as const) {
      const inner = r[wrap];
      if (inner && typeof inner === "object" && !Array.isArray(inner)) {
        candidates.push(inner);
        const innerObj = inner as Record<string, unknown>;
        for (const k of nestedKeys) {
          if (innerObj[k] != null) candidates.push(maybeJsonParse(innerObj[k]));
        }
      }
    }
  }
  for (const c of candidates) {
    const m = tryAsBookLedgerMappingDict(c);
    if (m && Object.keys(m).length > 0) return m;
  }
  return deepFindMappingDict(unwrapped, 0, 10);
}

export function processTrialBalanceMappingDict(
  mappingData: Record<string, any[]>,
  includeAmounts: boolean,
): {
  mappingRows: TrialBalanceMappingRow[];
  amountsData: TrialBalanceAmountsRow[] | null;
  availableDates: string[];
  data: Record<string, any[]> | null;
} {
  const mappingRows: TrialBalanceMappingRow[] = [];
  const amountsMap: Record<
    string,
    { bookName: string; ledgerId: number; ledgerName: string; balances: Record<string, number> }
  > = {};
  const allDates = new Set<string>();

  for (const [bookName, bookEntries] of Object.entries(mappingData)) {
    if (!Array.isArray(bookEntries)) continue;
    const normalized = bookName === "Non SEZ" ? "Non-SEZ" : bookName;

    for (const entry of bookEntries) {
      if (!entry || typeof entry !== "object") continue;
      const ledgerId = entry.ledger_id ?? entry.ledgerId ?? entry.LedgerId ?? 0;
      const ledgerName = String(
        entry.ledger_name ?? entry.ledgerName ?? entry.LedgerName ?? "",
      );

      mappingRows.push({
        bookName: normalized,
        ledgerId,
        ledgerName,
        noteLine: String(entry.note_line ?? entry.noteLine ?? ""),
        noteLineId: entry.note_line_id ?? entry.noteLineId ?? 0,
        fsSubLine: String(entry.fs_sub_line ?? entry.fsSubLine ?? ""),
        fsSubLineId: entry.fs_sub_line_id ?? entry.fsSubLineId ?? 0,
        fsLine: String(entry.fs_line ?? entry.fsLine ?? ""),
        fsLineId: entry.fs_line_id ?? entry.fsLineId ?? 0,
        type: String(entry.type ?? ""),
        par: String(entry.par ?? ""),
        mapping: String(entry.mapping ?? ""),
      });

      if (includeAmounts) {
        const amountField = entry.amount;
        if (amountField && typeof amountField === "object" && !Array.isArray(amountField)) {
          const key = `${normalized}|${ledgerId}|${ledgerName}`;
          if (!amountsMap[key]) {
            amountsMap[key] = {
              bookName: normalized,
              ledgerId,
              ledgerName,
              balances: {},
            };
          }
          const rec = amountsMap[key];
          for (const [dateKey, amt] of Object.entries(amountField as Record<string, unknown>)) {
            allDates.add(dateKey);
            const prev = rec.balances[dateKey] ?? 0;
            try {
              rec.balances[dateKey] = prev + Number(amt ?? 0);
            } catch {
              /* ignore */
            }
          }
        }
      }
    }
  }

  const availableDates =
    includeAmounts && allDates.size > 0
      ? [...allDates].sort((a, b) => parseDateKeyForSort(b) - parseDateKeyForSort(a))
      : [];

  return {
    mappingRows,
    amountsData: includeAmounts ? Object.values(amountsMap) : null,
    availableDates,
    data: includeAmounts ? mappingData : null,
  };
}
